/**
 * Pure step model and product payload for the New product wizard.
 * Keep STORE_CATEGORIES / PLATFORM_GROUPS in sync with src/services/product-identity.js.
 */

export const STAGES = [
  { id: 'live', label: 'Live' },
  { id: 'beta', label: 'Beta' },
  { id: 'pre-launch', label: 'Pre-launch' },
];

export const STORE_CATEGORIES = [
  { id: 'games', label: 'Games' },
  { id: 'business', label: 'Business' },
  { id: 'productivity', label: 'Productivity' },
  { id: 'entertainment', label: 'Entertainment' },
  { id: 'music', label: 'Music' },
  { id: 'photo-video', label: 'Photo & Video' },
  { id: 'health-fitness', label: 'Health & Fitness' },
  { id: 'lifestyle', label: 'Lifestyle' },
  { id: 'education', label: 'Education' },
  { id: 'finance', label: 'Finance' },
  { id: 'shopping', label: 'Shopping' },
  { id: 'social-networking', label: 'Social Networking' },
  { id: 'news', label: 'News' },
  { id: 'sports', label: 'Sports' },
  { id: 'travel', label: 'Travel' },
  { id: 'utilities', label: 'Utilities' },
  { id: 'developer-tools', label: 'Developer Tools' },
  { id: 'kids', label: 'Kids' },
];

export const PLATFORM_GROUPS = [
  {
    id: 'phone',
    label: 'Phone and tablet',
    items: [
      { id: 'ios', label: 'iOS' },
      { id: 'ipados', label: 'iPadOS' },
      { id: 'android', label: 'Android' },
    ],
  },
  {
    id: 'desktop',
    label: 'Desktop',
    items: [
      { id: 'macos', label: 'macOS' },
      { id: 'windows', label: 'Windows' },
      { id: 'linux', label: 'Linux' },
      { id: 'web', label: 'Web' },
    ],
  },
  {
    id: 'tv',
    label: 'TV',
    items: [
      { id: 'appletv', label: 'Apple TV' },
      { id: 'firetv', label: 'Fire TV' },
      { id: 'androidtv', label: 'Android TV' },
      { id: 'roku', label: 'Roku' },
    ],
  },
  {
    id: 'other',
    label: 'Other',
    items: [
      { id: 'watchos', label: 'watchOS' },
      { id: 'wearos', label: 'Wear OS' },
      { id: 'visionos', label: 'visionOS' },
    ],
  },
];

export const STEPS = [
  { n: 1, label: 'Product details' },
  { n: 2, label: 'Context files' },
  { n: 3, label: 'First connector' },
];

/** Depth weights — same as src/services/depth.js DEPTH_WEIGHTS. */
export const DEPTH_WEIGHTS = { analytics: 35, code: 25, revenue: 25, ads: 15 };

/**
 * In-flow connect sources (stay inside onboarding). Design source → provider slug.
 * BigQuery uses the Firebase connector (BigQuery warehouse path) — there is no
 * separate bigquery provider row.
 */
export const IN_FLOW_SOURCES = {
  ga4: { provider: 'google-analytics', title: 'Google Analytics 4', google: true },
  bigquery: { provider: 'firebase', title: 'BigQuery', google: true },
  firebase: { provider: 'firebase', title: 'Firebase', google: true },
  mixpanel: { provider: 'mixpanel', title: 'Mixpanel', google: false },
  posthog: { provider: 'posthog', title: 'PostHog', google: false },
};

export const DRAFT_KEY = 'bonnieos.wizCreateDraft';

/** @returns {object} */
export function freshState() {
  return {
    step: 1,
    productId: '',
    name: '',
    intro: '',
    stage: 'live',
    storeCategory: '',
    platforms: [],
    desc: '',
    explicitlyNot: '',
    audience: '',
    saved: null,
    files: [],
    jobs: [],
    connectSource: '',
  };
}

/** @param {string} name */
export function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

/** @param {string[]} platforms @param {string} id */
export function togglePlatform(platforms, id) {
  const set = new Set(platforms || []);
  if (set.has(id)) set.delete(id);
  else set.add(id);
  return [...set];
}

/** @param {number} step */
export function progressPct(step) {
  const n = Math.min(3, Math.max(1, Number(step) || 1));
  return Math.round((n / 3) * 100);
}

/**
 * @param {number} step
 * @param {string} productId
 */
export function stepItems(step, productId) {
  const locked = !productId;
  return STEPS.map((item) => ({
    ...item,
    on: step === item.n,
    done: step > item.n,
    locked: locked && item.n > 1,
  }));
}

/**
 * First-product flow: no other products exist besides the one being set up.
 * @param {object[]} products
 * @param {string} [productId]
 */
export function isFirstProductFlow(products, productId) {
  const others = (products || []).filter((item) => item && item.id && item.id !== productId);
  return others.length === 0;
}

/**
 * Step 1 of first-product flow has no sidebar (design + classic zero-product).
 * @param {object[]} products
 * @param {string} productId
 * @param {number} step
 */
export function hideWizardSidebar(products, productId, step) {
  return isFirstProductFlow(products, productId) && Number(step) === 1;
}

/**
 * Welcome badge only on step 1 of the first-product flow.
 * @param {object[]} products
 * @param {string} productId
 * @param {number} step
 */
export function showWelcomeBadge(products, productId, step) {
  return isFirstProductFlow(products, productId) && Number(step) === 1;
}

/**
 * @param {string} [search]
 * @returns {{ productId: string, step: number, connectSource: string }}
 */
export function readUrlState(search) {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const step = Number(params.get('step')) || 1;
  const connect = String(params.get('connect') || '').toLowerCase();
  return {
    productId: params.get('product_id') || '',
    step: step >= 1 && step <= 3 ? step : 1,
    connectSource: IN_FLOW_SOURCES[connect] ? connect : '',
  };
}

/**
 * @param {string} productId
 * @param {number} step
 * @param {string} [connectSource]
 */
export function wizardPath(productId, step, connectSource) {
  const params = new URLSearchParams();
  if (productId) params.set('product_id', productId);
  if (step && step !== 1) params.set('step', String(step));
  if (connectSource && IN_FLOW_SOURCES[connectSource]) {
    params.set('connect', connectSource);
    if (!params.has('step')) params.set('step', '3');
  }
  const qs = params.toString();
  return qs ? `/app/products/new?${qs}` : '/app/products/new';
}

/**
 * Topbar crumb for the wizard.
 * @param {number} step
 * @param {string} [connectSource]
 */
export function wizardCrumb(step, connectSource) {
  if (connectSource && IN_FLOW_SOURCES[connectSource]) {
    return `New product — connect ${IN_FLOW_SOURCES[connectSource].title}`;
  }
  if (Number(step) === 2) return 'New product — context';
  if (Number(step) === 3) return 'New product — connectors';
  return 'New product';
}

/**
 * Resolve a catalogue item for an in-flow design source.
 * @param {object[]} items
 * @param {string} source
 */
export function itemForSource(items, source) {
  const meta = IN_FLOW_SOURCES[source];
  if (!meta) return null;
  return (items || []).find((item) => item && item.name === meta.provider) || null;
}

/** @param {object} state */
export function writeDraft(state) {
  if (!state || state.productId) {
    clearDraft();
    return;
  }
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
      name: state.name || '',
      intro: state.intro || '',
      stage: state.stage || 'live',
      platforms: Array.isArray(state.platforms) ? state.platforms : [],
      storeCategory: state.storeCategory || '',
      savedAt: Date.now(),
    }));
  } catch {
    /* ignore */
  }
}

export function clearDraft() {
  try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
}

/** @param {object} state */
export function applyDraft(state) {
  if (!state || state.productId) return false;
  let raw = null;
  try { raw = sessionStorage.getItem(DRAFT_KEY); } catch { return false; }
  if (!raw) return false;
  let draft = null;
  try { draft = JSON.parse(raw); } catch { return false; }
  if (!draft || typeof draft !== 'object') return false;
  state.name = draft.name || '';
  state.intro = draft.intro || '';
  state.stage = draft.stage || 'live';
  state.platforms = Array.isArray(draft.platforms) ? draft.platforms : [];
  state.storeCategory = draft.storeCategory || '';
  return !!(state.name || state.intro || state.storeCategory || state.platforms.length);
}

/**
 * @param {object} product
 * @param {object[]} [docs]
 */
export function stateFromProduct(product, docs) {
  let platforms = product && product.platforms;
  if (typeof platforms === 'string') {
    try { platforms = JSON.parse(platforms); } catch { platforms = []; }
  }
  return {
    ...freshState(),
    productId: product.id || '',
    name: product.name || '',
    intro: product.short_intro || '',
    desc: product.description || '',
    explicitlyNot: product.explicitly_not || '',
    audience: product.target_user_base || '',
    stage: product.stage || 'live',
    storeCategory: product.store_category || '',
    platforms: Array.isArray(platforms)
      ? platforms.map((item) => String(item).toLowerCase())
      : [],
    saved: {
      icon: product.icon,
      color: product.color,
      category: product.category,
      slug: product.slug,
      status: product.status,
    },
    files: Array.isArray(docs) ? docs : [],
  };
}

/**
 * Body for POST /products or PUT /products/:id.
 * @param {object} state
 * @returns {{ error?: string, body?: object, editing?: boolean }}
 */
export function payloadFromState(state) {
  const name = String((state && state.name) || '').trim();
  if (!name) return { error: 'Name the product first' };
  const editing = !!(state && state.productId);
  const body = {
    name,
    slug: (editing && state.saved && state.saved.slug) || slugify(name) || 'product',
    description: (state && state.desc) || '',
    stage: (state && state.stage) || 'live',
    short_intro: (state && state.intro) || '',
    explicitly_not: (state && state.explicitlyNot) || '',
    target_user_base: (state && state.audience) || '',
    platforms: (state && state.platforms) || [],
    store_category: (state && state.storeCategory) || '',
    category: (editing && state.saved && state.saved.category) || 'app',
  };
  if (editing && state.saved) {
    body.icon = state.saved.icon;
    body.color = state.saved.color;
    body.status = state.saved.status;
  }
  return { body, editing };
}

/**
 * Primary analytics row for the connectors step (design order).
 * BigQuery maps to the firebase provider (warehouse path).
 */
export const PRIMARY_CONNECTORS = [
  { source: 'ga4', name: 'google-analytics', label: 'Connect GA4', primary: true },
  { source: 'bigquery', name: 'firebase', label: 'BigQuery', primary: false },
  { source: 'mixpanel', name: 'mixpanel', label: 'Mixpanel', primary: false },
  { source: 'posthog', name: 'posthog', label: 'PostHog', primary: false },
  { source: 'firebase', name: 'firebase', label: 'Firebase', primary: false },
  { source: '', name: 'mongo-readonly', label: 'MongoDB', primary: false },
];

/** Payoff groups after analytics — labels/weights match DEPTH_WEIGHTS. */
export const PAYOFF_GROUPS = [
  {
    key: 'code',
    title: 'Code and workspace',
    gain: `+${DEPTH_WEIGHTS.code}% depth`,
    blurb: 'GitHub, ClickUp, Slack and similar. Ties releases and team context to what changed in the numbers.',
    names: ['github', 'clickup', 'slack', 'sharepoint'],
  },
  {
    key: 'revenue',
    title: 'Revenue, stores and distribution',
    gain: `+${DEPTH_WEIGHTS.revenue}% depth`,
    blurb: 'RevenueCat, Stripe, App Store Connect, Google Play, AppFigures, Amazon. Money and store performance.',
    names: ['revenuecat', 'stripe', 'app-store-connect', 'google-play', 'appfigures', 'amazon-reports'],
  },
  {
    key: 'ads',
    title: 'Ads and acquisition',
    gain: `+${DEPTH_WEIGHTS.ads}% depth`,
    blurb: 'Apple Search Ads, Google Ads, AdMob, AppsFlyer, AppLovin. Spend against retention — which channel buys users who stay.',
    names: ['apple-search-ads', 'google-ads', 'admob', 'appsflyer', 'applovin'],
  },
];

/**
 * Group catalogue items for the first-connector ladder.
 * Analytics first (required), then code / revenue / ads payoff groups.
 * @param {object[]} items
 */
export function connectorRungs(items) {
  const list = items || [];
  const byName = new Map(list.map((item) => [item.name, item]));

  const analyticsItems = PRIMARY_CONNECTORS.map((slot) => {
    const found = byName.get(slot.name);
    return {
      ...(found || {
        name: slot.name,
        title: slot.label,
        category: 'Analytics',
        soon: true,
        connected: false,
      }),
      source: slot.source,
      label: slot.label,
      primary: slot.primary,
      inFlow: Boolean(slot.source && IN_FLOW_SOURCES[slot.source]),
    };
  });

  const payoff = PAYOFF_GROUPS.map((group) => ({
    key: group.key,
    title: group.title,
    gain: group.gain,
    required: false,
    blurb: group.blurb,
    items: group.names.map((name) => {
      const found = byName.get(name);
      return found || {
        name,
        title: name,
        category: group.key === 'code' ? 'Code' : (group.key === 'ads' ? 'Ads' : 'Revenue'),
        soon: true,
        connected: false,
      };
    }),
  }));

  return [
    {
      key: 'analytics',
      title: 'Behaviour analytics',
      gain: `+${DEPTH_WEIGHTS.analytics}% depth`,
      required: true,
      blurb: 'GA4, Firebase, Mixpanel or BigQuery. Best starting point — but any live connector unlocks chat.',
      items: analyticsItems,
    },
    ...payoff,
  ];
}

/** @param {string} name */
export function stageRadios(stage) {
  return STAGES.map((item) => ({
    ...item,
    on: item.id === (stage || 'live'),
  }));
}
